import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Loader2, Smartphone, ChevronDown, ChevronUp } from 'lucide-react';

export default function Login() {
  const { signIn, signUp } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        if (!username.trim()) {
          throw new Error('Username is required');
        }
        await signUp(email, password, username);
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      const msg = err.message || 'An error occurred';
      if (msg.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please try again or sign up.');
      } else if (msg.includes('Email not confirmed')) {
        setError('Please check your email to confirm your account.');
      } else if (msg.includes('User already registered')) {
        setError('This email is already registered. Please sign in.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#08080c] flex flex-col items-center justify-center p-4" data-testid="login-screen">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="flex flex-col items-center">
            <img 
              src="/owl-logo.png" 
              alt="Sage Owl" 
              className="w-28 h-28 mb-1"
              style={{ filter: 'drop-shadow(0 0 20px rgba(0, 212, 255, 0.5))' }}
              data-testid="sage-owl-icon"
            />
            <h1 
              className="font-mono text-4xl font-bold tracking-tight select-none"
              style={{ 
                color: '#00d4ff',
                textShadow: '0 0 20px rgba(0, 212, 255, 0.5), 0 0 40px rgba(0, 212, 255, 0.2)'
              }}
              data-testid="sage-logo"
            >
              SAGE
            </h1>
          </div>
          <div className="h-px w-24 mx-auto mt-4 bg-gradient-to-r from-transparent via-[#00d4ff] to-transparent opacity-50" />
          <p className="text-[#4a4a60] font-mono text-[10px] mt-3 tracking-[0.3em] uppercase">
            Intelligence Platform
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {isSignUp && (
            <div>
              <label className="block font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em] mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] px-4 py-3 font-mono text-sm focus:border-[#00d4ff] focus:shadow-[0_0_10px_rgba(0,212,255,0.2)] transition-all outline-none"
                placeholder="Enter username"
                data-testid="username-input"
              />
            </div>
          )}

          <div>
            <label className="block font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em] mb-2">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] px-4 py-3 font-mono text-sm focus:border-[#00d4ff] focus:shadow-[0_0_10px_rgba(0,212,255,0.2)] transition-all outline-none"
              placeholder="Enter email"
              required
              data-testid="email-input"
            />
          </div>

          <div>
            <label className="block font-mono text-[10px] text-[#00d4ff] uppercase tracking-[0.2em] mb-2">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#0d0d12] border border-[#1a1a24] text-[#e8e8f0] px-4 py-3 font-mono text-sm focus:border-[#00d4ff] focus:shadow-[0_0_10px_rgba(0,212,255,0.2)] transition-all outline-none"
              placeholder="Enter password"
              required
              data-testid="password-input"
            />
          </div>

          {error && (
            <div className="border border-[#ff3366]/30 bg-[#ff3366]/5 text-[#ff3366] px-4 py-3 text-xs font-mono" data-testid="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full border border-[#00d4ff] text-[#00d4ff] font-mono text-xs uppercase tracking-[0.2em] py-3.5 transition-all hover:bg-[#00d4ff]/10 hover:shadow-[0_0_20px_rgba(0,212,255,0.3)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-button"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSignUp ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        {/* Toggle */}
        <div className="text-center mt-8">
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
            }}
            className="text-[#4a4a60] hover:text-[#00d4ff] font-mono text-[10px] uppercase tracking-[0.15em] transition-colors"
            data-testid="toggle-auth-mode"
          >
            {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
          </button>
        </div>

        {/* Install as App Guide */}
        <div className="mt-10 border border-[#1a1a24] bg-[#0d0d12] rounded-lg overflow-hidden" data-testid="install-guide">
          <button
            onClick={() => setShowInstallGuide(!showInstallGuide)}
            className="w-full p-4 flex items-center justify-between hover:bg-[#111118] transition-colors"
          >
            <div className="flex items-center gap-3">
              <Smartphone className="w-4 h-4 text-[#00d4ff]" />
              <span className="font-mono text-[10px] text-[#00d4ff] uppercase tracking-wider">Install as App</span>
            </div>
            {showInstallGuide ? (
              <ChevronUp className="w-4 h-4 text-[#4a4a60]" />
            ) : (
              <ChevronDown className="w-4 h-4 text-[#4a4a60]" />
            )}
          </button>
          
          {showInstallGuide && (
            <div className="p-4 pt-0 space-y-4">
              {/* iPhone/iPad */}
              <div className="bg-[#08080c] border border-[#1a1a24] rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#e8e8f0]" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <span className="font-mono text-[10px] text-[#e8e8f0] uppercase tracking-wider">iPhone / iPad</span>
                </div>
                <ol className="text-[11px] text-[#7a7a9a] space-y-1.5 pl-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d4ff] font-mono">1.</span>
                    <span>Open this page in <strong className="text-[#e8e8f0]">Safari</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d4ff] font-mono">2.</span>
                    <span>Tap the <strong className="text-[#e8e8f0]">Share</strong> button (square with arrow)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d4ff] font-mono">3.</span>
                    <span>Scroll down and tap <strong className="text-[#e8e8f0]">"Add to Home Screen"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#00d4ff] font-mono">4.</span>
                    <span>Tap <strong className="text-[#e8e8f0]">"Add"</strong> to install</span>
                  </li>
                </ol>
              </div>
              
              {/* Android */}
              <div className="bg-[#08080c] border border-[#1a1a24] rounded p-3">
                <div className="flex items-center gap-2 mb-2">
                  <svg viewBox="0 0 24 24" className="w-4 h-4 text-[#3ddc84]" fill="currentColor">
                    <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z"/>
                  </svg>
                  <span className="font-mono text-[10px] text-[#3ddc84] uppercase tracking-wider">Android</span>
                </div>
                <ol className="text-[11px] text-[#7a7a9a] space-y-1.5 pl-4">
                  <li className="flex items-start gap-2">
                    <span className="text-[#3ddc84] font-mono">1.</span>
                    <span>Open this page in <strong className="text-[#e8e8f0]">Chrome</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3ddc84] font-mono">2.</span>
                    <span>Tap the <strong className="text-[#e8e8f0]">⋮ menu</strong> (three dots)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3ddc84] font-mono">3.</span>
                    <span>Tap <strong className="text-[#e8e8f0]">"Add to Home screen"</strong> or <strong className="text-[#e8e8f0]">"Install app"</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#3ddc84] font-mono">4.</span>
                    <span>Tap <strong className="text-[#e8e8f0]">"Install"</strong> to confirm</span>
                  </li>
                </ol>
              </div>
              
              <p className="text-[9px] text-[#4a4a60] text-center">
                Installing as an app gives you the full experience with offline access and push notifications.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
